import { motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

interface AtmosphereSectionProps {
  title: string
  text: string
  imageSrc: string
  imageAlt: string
  variant?: 'light' | 'dark'
}

export function AtmosphereSection({
  title,
  text,
  imageSrc,
  imageAlt,
  variant = 'light',
}: AtmosphereSectionProps) {
  const shouldReduceMotion = useReducedMotion()
  const isLight = variant === 'light'

  const baseTransition = { duration: 0.6, ease: 'easeOut' as const }
  const fadeUp = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 28 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-80px' },
        transition: baseTransition,
      }

  const paragraphs = text
    .replace(/\\n/g, '\n')
    .split('\n')
    .filter((line) => line.trim() !== '')

  // Prevent the apostrophe from visually collapsing into neighbouring letters
  // when the display face is set with tight tracking.
  const renderTitle = (value: string) => {
    const parts = value.split(/(['’])/g)
    return parts.map((part, index) => {
      if (part === "'" || part === '’') {
        return (
          <span key={index} className="inline-block pr-[0.06em]">
            {part}
          </span>
        )
      }
      return <span key={index}>{part}</span>
    })
  }

  return (
    <section
      className={cn(
        'relative overflow-hidden px-4 py-[clamp(4.5rem,10vw,8rem)] sm:px-6 lg:px-8',
        isLight
          ? 'bg-maia-soft text-ink'
          : 'bg-gradient-to-br from-maia via-maia to-maia-dark text-white'
      )}
      aria-labelledby="atmosphere-heading"
    >
      {/* Decorative background orbs */}
      <div
        className={cn(
          'pointer-events-none absolute -right-40 -top-40 h-[28rem] w-[28rem] rounded-full blur-3xl sm:h-[40rem] sm:w-[40rem]',
          isLight ? 'bg-maia/10' : 'bg-white/10'
        )}
        aria-hidden="true"
      />
      <div
        className={cn(
          'pointer-events-none absolute -bottom-32 -left-32 h-[24rem] w-[24rem] rounded-full blur-3xl sm:h-[32rem] sm:w-[32rem]',
          isLight ? 'bg-maia-light/60' : 'bg-maia-dark/25'
        )}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-6xl">
        <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-14">
          {/* Copy */}
          <motion.div
            {...fadeUp}
            className="order-2 lg:order-1 lg:col-span-7"
          >
            <h2
              id="atmosphere-heading"
              className={cn(
                'font-display text-[clamp(2.75rem,9vw,5.5rem)] font-extrabold leading-[0.9] tracking-tighter',
                isLight ? 'text-ink' : 'text-white'
              )}
            >
              {renderTitle(title)}
            </h2>

            {/* Organic underline */}
            <div className="relative mt-4 h-5 w-[clamp(7rem,24vw,15rem)] text-maia">
              <svg
                viewBox="0 0 200 20"
                fill="none"
                aria-hidden="true"
                className="h-full w-full"
                preserveAspectRatio="none"
              >
                <motion.path
                  d="M2 12C30 12 30 4 60 4s30 14 60 14 30-10 60-10"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeLinecap="round"
                  fill="none"
                  initial={shouldReduceMotion ? {} : { pathLength: 0, opacity: 0 }}
                  whileInView={shouldReduceMotion ? {} : { pathLength: 1, opacity: 1 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.9, ease: 'easeInOut', delay: 0.15 }}
                />
              </svg>
            </div>

            <div
              className={cn(
                'mt-8 max-w-xl space-y-5 text-lg leading-relaxed sm:text-xl',
                isLight ? 'text-muted' : 'text-white/90'
              )}
            >
              {paragraphs.map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            {...fadeUp}
            className="order-1 lg:order-2 lg:col-span-5"
          >
            <div className="relative mx-auto max-w-sm lg:max-w-none">
              <div
                className={cn(
                  'absolute inset-0 rotate-3 rounded-[2rem]',
                  isLight ? 'bg-cord' : 'bg-white/15'
                )}
                aria-hidden="true"
              />
              <div
                className={cn(
                  'relative -rotate-2 rounded-[2rem] border-[6px] p-2 shadow-2xl',
                  isLight
                    ? 'border-white bg-white'
                    : 'border-white/90 bg-white/90'
                )}
              >
                <img
                  src={imageSrc}
                  alt={imageAlt}
                  className="aspect-[4/5] w-full rounded-[1.5rem] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
